/**
 * SmartUploadConfirmationDialog Component
 * Dialog for confirming AI-matched certificate uploads
 * Two-panel layout: Left for data/matching, Right for document preview
 */

import { memo, useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  X,
  AlertTriangle,
  CalendarIcon,
  Sparkles,
  Download,
  FileText,
  Loader2,
  ExternalLink,
  Search,
  Check,
  ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';
import { gcsGetSignedReadUrl } from '@/lib/gcsStorage';
import { SimplePDFViewer } from '@/components/SimplePDFViewer';
import { scoreMembersByQuery, scoreRequirementsByQuery } from '@/lib/certificateSmartMatch';

// ---------------------------------------------------------------------------
// BOHSA course URL helper
// ---------------------------------------------------------------------------

const BOHSA_COURSES_API =
  'https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses';
const BOHSA_API_KEY = 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX';

const slugCache = new Map();

async function fetchBohsaCourseSlug(courseId, courseName) {
  if (!courseId) return null;
  if (slugCache.has(courseId)) return slugCache.get(courseId);

  try {
    const res = await fetch(
      `${BOHSA_COURSES_API}?search=${encodeURIComponent(courseName || '')}`,
      {
        headers: {
          'Content-Type': 'application/json',
          apikey: BOHSA_API_KEY
        }
      }
    );
    if (!res.ok) return null;

    const json = await res.json();
    const courses = json?.data || [];
    const match = courses.find(c => String(c.id) === String(courseId));
    const slug = match?.slug || null;
    if (slug) slugCache.set(courseId, slug);
    return slug;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Member display helpers
// ---------------------------------------------------------------------------

function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function memberSubtitle(member) {
  if (!member) return null;
  const bits = [member.role, member.department, member.email].filter(Boolean);
  return bits.length ? bits.join(' · ') : null;
}

function requirementSubtitle(req) {
  if (!req?.spaces?.length) return null;
  if (req.spaces.length === 1) return `In ${req.spaces[0].name}`;
  return `In ${req.spaces.length} spaces`;
}

// ---------------------------------------------------------------------------
// BohsaLinkButton — async slug fetch + open in new tab
// ---------------------------------------------------------------------------

function BohsaLinkButton({ courseId, courseName }) {
  const [slug, setSlug] = useState(() => slugCache.get(courseId) || null);

  useEffect(() => {
    if (!slug) {
      fetchBohsaCourseSlug(courseId, courseName).then(s => {
        if (s) setSlug(s);
      });
    }
  }, [courseId, courseName, slug]);

  if (!slug) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        window.open(`https://bohsa.com/course/${slug}`, '_blank');
      }}
      className="flex-none p-1 rounded hover:bg-gray-100 transition-colors"
      title="View on bohsa.com"
    >
      <ExternalLink className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// AssignmentScopeInfo — shows which spaces the cert will be applied to
// ---------------------------------------------------------------------------

function AssignmentScopeInfo({ scope }) {
  if (!scope) return null;
  const { mode, eligibleSpaces = [], unassignedSpaces = [] } = scope;
  if (eligibleSpaces.length === 0) return null;

  const isStack = mode === 'stack';
  const allAssigned = unassignedSpaces.length === 0;

  if (!isStack) {
    // Single space — keep it simple
    if (allAssigned) return null; // No warning needed, already assigned
    return (
      <div className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-none mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-900">New assignment</p>
          <p className="text-amber-700">
            This qualification will be assigned in {eligibleSpaces[0]?.name || 'this space'}.
          </p>
        </div>
      </div>
    );
  }

  // Stack/global view
  return (
    <div className="rounded-md border bg-gray-50 px-3 py-2.5 text-sm space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Applies to {eligibleSpaces.length} {eligibleSpaces.length === 1 ? 'space' : 'spaces'}
        </span>
      </div>
      <div className="space-y-1">
        {eligibleSpaces.map(s => {
          const isNew = unassignedSpaces.some(u => u.id === s.id);
          return (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <span className={`h-1.5 w-1.5 rounded-full ${isNew ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              <span className="flex-1 truncate text-gray-700">{s.name}</span>
              <span className="text-[11px] text-gray-400">
                {isNew ? 'new assignment' : 'already assigned'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CandidateDropdown — unified search + candidates list
// ---------------------------------------------------------------------------

function CandidateDropdown({
  type,          // 'member' | 'requirement'
  selected,      // current selection
  extractedData, // raw AI-extracted data — used for the initial query
  pool,          // searchable pool (current view + company-wide)
  onSelect,      // callback(item)
  open,
  onOpenChange
}) {
  const isMember = type === 'member';
  const inputRef = useRef(null);

  // Detected text from the AI — used to pre-fill the search input
  const detectedText = useMemo(() => {
    if (!extractedData) return '';
    if (isMember) {
      const fn = Array.isArray(extractedData.first_name) ? extractedData.first_name[0] : extractedData.first_name;
      const ln = Array.isArray(extractedData.last_name) ? extractedData.last_name[0] : extractedData.last_name;
      return [fn, ln].filter(Boolean).join(' ') || '';
    }
    const ct = Array.isArray(extractedData.course_title) ? extractedData.course_title[0] : extractedData.course_title;
    return ct || '';
  }, [extractedData, isMember]);

  const [query, setQuery] = useState(detectedText);

  // Pre-fill query each time the popover opens, focus & select-all so user can quickly retype
  useEffect(() => {
    if (open) {
      setQuery(detectedText);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [open, detectedText]);

  // Always score against the pool using the current query
  const displayItems = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed || !pool?.length) return [];

    const scored = isMember
      ? scoreMembersByQuery(trimmed, pool)
      : scoreRequirementsByQuery(trimmed, pool);

    return scored.map(s => ({
      item: isMember ? s.member : s.requirement,
      score: s.score
    }));
  }, [query, pool, isMember]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="flex-none h-8 text-xs gap-1">
          Change
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="end" sideOffset={4}>
        {/* Search input — pre-filled with detected text on open */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isMember ? 'Search team members...' : 'Search qualifications...'}
              className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-300 placeholder:text-gray-400"
            />
          </div>
          {detectedText && (
            <p className="px-1 pt-1.5 text-[10px] text-gray-400 truncate">
              From certificate: &ldquo;{detectedText}&rdquo;
            </p>
          )}
        </div>

        {/* Results list */}
        <div className="max-h-[260px] overflow-y-auto">
          {displayItems.length === 0 ? (
            <p className="px-3 py-6 text-sm text-gray-400 text-center">
              {query.trim() ? 'No results found' : 'Type to search'}
            </p>
          ) : (
            displayItems.map(({ item, score }) => {
              const isSelected = item.id === selected?.id;
              const pct = score != null ? Math.round(score * 100) : null;
              const subtitle = isMember ? memberSubtitle(item) : requirementSubtitle(item);

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onSelect(item);
                    onOpenChange(false);
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${
                    isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Avatar (members) or check indicator (requirements) */}
                  {isMember ? (
                    <Avatar className="h-7 w-7 flex-none">
                      {item.avatar_url && <AvatarImage src={item.avatar_url} alt={item.name} />}
                      <AvatarFallback className="text-[10px] bg-gray-200 text-gray-600">
                        {initialsOf(item.name)}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="flex-none w-4">
                      {isSelected && <Check className="h-3.5 w-3.5 text-gray-600" />}
                    </div>
                  )}

                  {/* Name + subtitle */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{item.name}</span>
                      {item._outOfView && (
                        <span className="flex-none text-[9px] uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1 py-px">
                          other
                        </span>
                      )}
                    </p>
                    {subtitle && (
                      <p className="text-[11px] text-gray-400 truncate">{subtitle}</p>
                    )}
                  </div>

                  {/* Score */}
                  {pct != null && (
                    <span className="flex-none text-xs text-gray-400 tabular-nums">{pct}%</span>
                  )}

                  {/* Member profile link */}
                  {isMember && (item.user_id || item.id) && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/profile/${item.user_id || item.id}`, '_blank');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          window.open(`/profile/${item.user_id || item.id}`, '_blank');
                        }
                      }}
                      className="flex-none p-1 rounded hover:bg-gray-200 transition-colors cursor-pointer"
                      title="View profile in new tab"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
                    </span>
                  )}

                  {/* BOHSA link for verified courses */}
                  {!isMember && item.course_link_type === 'verified' && item.course_link_id && (
                    <BohsaLinkButton courseId={item.course_link_id} courseName={item.name} />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

const SmartUploadConfirmationDialog = memo(({
  open,
  data,
  onConfirm,
  onDecline,
  teamMembers = [],
  requirements = []
}) => {
  const [expiryDate, setExpiryDate] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [memberPopoverOpen, setMemberPopoverOpen] = useState(false);
  const [requirementPopoverOpen, setRequirementPopoverOpen] = useState(false);
  const [bohsaSlug, setBohsaSlug] = useState(null);

  const fileType = useMemo(() => {
    if (!data?.fileName) return 'unknown';
    const ext = data.fileName.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    return 'unknown';
  }, [data?.fileName]);

  useEffect(() => {
    if (data) {
      if (data.expiryDate) {
        const date = new Date(data.expiryDate);
        setExpiryDate(!isNaN(date.getTime()) ? date : null);
      } else {
        setExpiryDate(null);
      }
      setCertificateNumber(data.certificateNumber || '');
      setSelectedMember(data.member);
      setSelectedRequirement(data.requirement);
      setCalendarOpen(false);
      setPreviewError(false);
      setBohsaSlug(null);
    }
  }, [data]);

  // Fetch BOHSA slug for the selected requirement
  useEffect(() => {
    if (selectedRequirement?.course_link_type === 'verified' && selectedRequirement?.course_link_id) {
      fetchBohsaCourseSlug(selectedRequirement.course_link_id, selectedRequirement.name).then(setBohsaSlug);
    } else {
      setBohsaSlug(null);
    }
  }, [selectedRequirement?.course_link_id, selectedRequirement?.course_link_type, selectedRequirement?.name]);

  useEffect(() => {
    const getPreviewUrl = async () => {
      if (!data?.tempFilePath) {
        setPreviewUrl(null);
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(false);

      try {
        const signedUrl = await gcsGetSignedReadUrl(data.tempFilePath);
        setPreviewUrl(signedUrl);
      } catch (error) {
        console.error('Error getting preview URL:', error);
        setPreviewError(true);
      } finally {
        setPreviewLoading(false);
      }
    };

    if (open && data?.tempFilePath) {
      getPreviewUrl();
    }
  }, [open, data?.tempFilePath]);

  // Match confidence for current selections (just the score number)
  const memberScore = useMemo(() => {
    if (!selectedMember || !data?.memberCandidates) return null;
    const c = data.memberCandidates.find(c => c.member.id === selectedMember.id);
    return c ? Math.round(c.score * 100) : null;
  }, [selectedMember, data?.memberCandidates]);

  const requirementScore = useMemo(() => {
    if (!selectedRequirement || !data?.requirementCandidates) return null;
    const c = data.requirementCandidates.find(c => c.requirement.id === selectedRequirement.id);
    return c ? Math.round(c.score * 100) : null;
  }, [selectedRequirement, data?.requirementCandidates]);

  if (!data) return null;

  const handleConfirm = () => {
    onConfirm({
      expiryDate: expiryDate ? expiryDate.toISOString().split('T')[0] : null,
      certificateNumber: certificateNumber || null,
      member: selectedMember,
      requirement: selectedRequirement
    });
  };

  const isValid = selectedMember && selectedRequirement;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline()}>
      <DialogContent
        className="!max-w-[1200px] w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Certificate Match Found</DialogTitle>

        {/* Header */}
        <div className="flex-none px-6 py-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Certificate Match Found</h2>
                <p className="text-sm text-gray-500">Review the detected details and confirm</p>
              </div>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Main Content - Two Panels */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Left Panel - Form */}
          <div className="w-[420px] flex-none flex flex-col border-r bg-white">
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* ---- Team Member ---- */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Team Member</Label>
                <div className="flex items-center gap-2.5 rounded-md border bg-gray-50/50 px-3 py-2">
                  {selectedMember ? (
                    <>
                      <Avatar className="h-8 w-8 flex-none">
                        {selectedMember.avatar_url && (
                          <AvatarImage src={selectedMember.avatar_url} alt={selectedMember.name} />
                        )}
                        <AvatarFallback className="text-[11px] bg-gray-200 text-gray-600">
                          {initialsOf(selectedMember.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{selectedMember.name}</p>
                        {memberSubtitle(selectedMember) && (
                          <p className="text-[11px] text-gray-400 truncate">{memberSubtitle(selectedMember)}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className="flex-1 text-sm italic text-gray-400">
                      {data.memberAmbiguous ? 'Multiple matches — please choose' : 'No match found'}
                    </span>
                  )}
                  {memberScore != null && (
                    <span className="flex-none text-xs text-gray-400 tabular-nums">{memberScore}%</span>
                  )}
                  {selectedMember && (selectedMember.user_id || selectedMember.id) && (
                    <button
                      onClick={() => window.open(`/profile/${selectedMember.user_id || selectedMember.id}`, '_blank')}
                      className="flex-none p-1 rounded hover:bg-gray-200 transition-colors"
                      title="View profile in new tab"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
                    </button>
                  )}
                  <CandidateDropdown
                    type="member"
                    selected={selectedMember}
                    extractedData={data.extractedData}
                    pool={data.memberPool || teamMembers}
                    onSelect={setSelectedMember}
                    open={memberPopoverOpen}
                    onOpenChange={setMemberPopoverOpen}
                  />
                </div>
                {data.memberAmbiguous && selectedMember == null && (
                  <p className="text-[11px] text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Two or more team members matched equally well — pick the right person
                  </p>
                )}
              </div>

              {/* ---- Qualification ---- */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Qualification</Label>
                <div className="flex items-center gap-2 rounded-md border bg-gray-50/50 px-3 py-2">
                  <span className="flex-1 min-w-0 text-sm truncate">
                    {selectedRequirement?.name || <span className="text-gray-400 italic">No match found</span>}
                  </span>
                  {requirementScore != null && (
                    <span className="flex-none text-xs text-gray-400 tabular-nums">{requirementScore}%</span>
                  )}
                  {bohsaSlug && (
                    <button
                      onClick={() => window.open(`https://bohsa.com/course/${bohsaSlug}`, '_blank')}
                      className="flex-none p-1 rounded hover:bg-gray-200 transition-colors"
                      title="View on bohsa.com"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
                    </button>
                  )}
                  <CandidateDropdown
                    type="requirement"
                    selected={selectedRequirement}
                    extractedData={data.extractedData}
                    pool={data.requirementPool || requirements}
                    onSelect={setSelectedRequirement}
                    open={requirementPopoverOpen}
                    onOpenChange={setRequirementPopoverOpen}
                  />
                </div>
              </div>

              {/* Assignment scope info */}
              <AssignmentScopeInfo scope={data.assignmentScope} />

              {/* Certificate Details */}
              <div className="space-y-4 pt-1">
                <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Certificate Details
                </Label>

                {/* Expiry Date */}
                <div className="space-y-1.5">
                  <Label htmlFor="expiry-date" className="text-sm font-medium">
                    Expiry Date <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="expiry-date"
                        variant="outline"
                        className={`w-full justify-start text-left font-normal h-9 ${!expiryDate && 'text-muted-foreground'}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {expiryDate ? format(expiryDate, 'PPP') : 'No expiry'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        startMonth={new Date(2020, 0)}
                        endMonth={new Date(2036, 11)}
                        selected={expiryDate}
                        onSelect={(date) => {
                          setExpiryDate(date);
                          setCalendarOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Certificate Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="cert-number" className="text-sm font-medium">
                    Certificate Number <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="cert-number"
                    value={certificateNumber}
                    onChange={(e) => setCertificateNumber(e.target.value)}
                    placeholder="Enter certificate number"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 min-w-0 flex flex-col bg-gray-100">
            {/* Preview Header */}
            <div className="flex-none px-4 py-3 border-b bg-white flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500 flex-none" />
              <span className="text-sm font-medium text-gray-700 truncate">{data.fileName}</span>
              <Badge variant="outline" className="text-xs flex-none">
                {fileType.toUpperCase()}
              </Badge>
            </div>

            {/* Preview Content */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {previewLoading ? (
                <div className="h-full flex flex-col items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500">Loading preview...</p>
                </div>
              ) : previewError ? (
                <div className="h-full flex flex-col items-center justify-center p-8">
                  <FileText className="h-16 w-16 text-gray-300" />
                  <p className="mt-4 text-sm font-medium text-gray-700">Unable to load preview</p>
                  <p className="text-xs text-gray-500">The document may not be accessible</p>
                  {previewUrl && (
                    <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')} className="mt-4">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  )}
                </div>
              ) : previewUrl ? (
                fileType === 'pdf' ? (
                  <SimplePDFViewer url={previewUrl} className="h-full" />
                ) : fileType === 'image' ? (
                  <div className="h-full flex items-center justify-center p-6 bg-neutral-900">
                    <img
                      src={previewUrl}
                      alt="Certificate"
                      className="max-w-full max-h-full object-contain rounded shadow-lg"
                      onError={() => setPreviewError(true)}
                    />
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center p-8">
                    <FileText className="h-16 w-16 text-gray-300" />
                    <p className="mt-4 text-sm text-gray-700">Preview not available for this file type</p>
                    <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')} className="mt-4">
                      <Download className="h-4 w-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                )
              ) : (
                <div className="h-full flex flex-col items-center justify-center">
                  <FileText className="h-16 w-16 text-gray-300" />
                  <p className="mt-4 text-sm text-gray-500">No preview available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-none px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onDecline} className="h-10 px-6">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid}
            className="h-10 px-6"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});

SmartUploadConfirmationDialog.displayName = 'SmartUploadConfirmationDialog';

export default SmartUploadConfirmationDialog;
